import React from 'react';
import { Controller } from 'react-hook-form';
import { observer } from 'mobx-react';
import Input from '@alfa-retail-react/components/input';
import { useFormContext } from '@alfa-retail-react/components/form-hook';

import { WEB_VISOR_DISABLE } from 'app/utils/AnalyticsUtils';
import { OrderFormValues } from 'app/logic/pages/order/OrderPage';
import { BirthPlaceFormStore } from 'app/logic/pages/order/BirthPlaceFormStore';
import { ValidationRules } from 'app/form/ValidationRules';
import { InputFieldEventHandler } from 'app/form/InputFieldEventHandler';

export interface BirthLocalityFormItemProps {
    /**
     * Хранилище состояния, содержадщего информацию о месте рождения пользователя.
     */
    formStore: BirthPlaceFormStore;
}

/**
 * Компонент выбора населённого пункта рождения.
 */
export const BirthLocalityFormItem = observer(
    ({ formStore }: BirthLocalityFormItemProps) => {
        const BIRTH_LOCALITY_FORM_ITEM_NAME = 'birthLocality';
        const { control, trigger, handleMultipleErrors, setFieldElement } =
            useFormContext<OrderFormValues>();
        const fieldHandler = new InputFieldEventHandler<OrderFormValues>(
            trigger,
        );

        const PLACEHOLDER = 'Название населённого пункта';

        const ALLOWED_CHARACTERS_VALIDATION_REGEXP =
            /^[a-zA-Zа-яА-ЯёЁ0-9\- .,'\\\/]+$/;
        const ALLOWED_CHARACTERS_VALIDATION_MESSAGE =
            'Допустимые символы: буквы кириллицей (А, б, в) или латиницей (A, b, c), цифры, дефис, слеш, точка, запятая, пробел, апостроф';

        return (
            <Controller<OrderFormValues>
                control={control}
                defaultValue={formStore.localityName ?? ''}
                name={BIRTH_LOCALITY_FORM_ITEM_NAME}
                rules={{
                    required: ValidationRules.required(),
                    maxLength: ValidationRules.maxLength(40),
                    pattern: ValidationRules.allowedCharacters(
                        ALLOWED_CHARACTERS_VALIDATION_REGEXP,
                        ALLOWED_CHARACTERS_VALIDATION_MESSAGE,
                    ),
                }}
                render={({ field, fieldState }) => {
                    const { onBlur, onClear, onChange } =
                        fieldHandler.getEventHandlers(
                            field,
                            BIRTH_LOCALITY_FORM_ITEM_NAME,
                            formStore.setLocalityName,
                        );

                    return (
                        <Input
                            clear
                            shouldUpperCase
                            inputClassName={WEB_VISOR_DISABLE}
                            ref={(element) => {
                                field.ref(element);
                                setFieldElement(
                                    BIRTH_LOCALITY_FORM_ITEM_NAME,
                                    element,
                                );
                            }}
                            size="l"
                            value={String(field.value)}
                            error={handleMultipleErrors(fieldState.error)}
                            label={PLACEHOLDER}
                            onBlur={onBlur}
                            onClear={onClear}
                            onChange={onChange}
                        />
                    );
                }}
            />
        );
    },
);
